/* =============================================================
   ImmersiveHUD.js — game-like HUD layered over the WebGL hero and
   parallax:
     • a fixed HUD frame (corner targeting brackets, sweeping
       scanline, cinematic vignette, faint CRT lines)
     • a cursor reticle that eases toward the pointer
     • a right-side scene rail that highlights the active "scene"
       and lets you jump between them like chapters
   Purely visual; honors prefers-reduced-motion and touch.
   ============================================================= */
import React, { useEffect, useRef, useState } from 'react';
import { usePrefersReducedMotion } from './landingFx';

const ImmersiveHUD = ({ scenes = [] }) => {
  const reduced = usePrefersReducedMotion();
  const [active, setActive] = useState(0);
  const reticleRef = useRef(null);

  // Track the active scene for the rail.
  useEffect(() => {
    if (!scenes.length || typeof IntersectionObserver === 'undefined') return undefined;
    const els = scenes.map((s) => document.getElementById(s.id)).filter(Boolean);
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            const idx = scenes.findIndex((s) => s.id === en.target.id);
            if (idx >= 0) setActive(idx);
          }
        });
      },
      { threshold: 0.4, rootMargin: '-20% 0px -40% 0px' }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [scenes]);

  // Cursor reticle (desktop / hover only).
  useEffect(() => {
    if (reduced) return undefined;
    if (window.matchMedia && window.matchMedia('(hover: none)').matches) return undefined;
    const el = reticleRef.current;
    if (!el) return undefined;
    let tx = window.innerWidth / 2;
    let ty = window.innerHeight / 2;
    let x = tx;
    let y = ty;
    let raf = null;
    const onMove = (e) => { tx = e.clientX; ty = e.clientY; el.style.opacity = '1'; };
    const loop = () => {
      x += (tx - x) * 0.2;
      y += (ty - y) * 0.2;
      el.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px)`;
      raf = window.requestAnimationFrame(loop);
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    raf = window.requestAnimationFrame(loop);
    return () => {
      window.removeEventListener('mousemove', onMove);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [reduced]);

  const jump = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  };

  return (
    <>
      <div className="hud" aria-hidden="true">
        <span className="hud-corner tl" />
        <span className="hud-corner tr" />
        <span className="hud-corner bl" />
        <span className="hud-corner br" />
        <div className="hud-vignette" />
        <div className="hud-scanline" />
      </div>

      {scenes.length > 0 && (
        <nav className="hud-rail" aria-label="Scenes">
          {scenes.map((s, i) => (
            <button
              key={s.id}
              type="button"
              className={`hud-rail-dot ${i === active ? 'active' : ''}`}
              onClick={() => jump(s.id)}
              aria-label={s.label}
            >
              <span className="hud-rail-label">{s.label}</span>
            </button>
          ))}
        </nav>
      )}

      {!reduced && <div className="hud-reticle" ref={reticleRef} aria-hidden="true" />}
    </>
  );
};

export default ImmersiveHUD;
